import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { BmiHubDirectory } from "@/components/bmi/BmiHubDirectory";
import { BmiCalculatorIsland } from "@/components/bmi/BmiCalculatorIsland";

const TITLE = "BMI Calculator Directory — 200 Age, Gender & Height Pages";
const DESC = "The full DrHint BMI matrix: 200 pages across standard, women, height-specific and South Asian (WHO Asian cut-off) BMI calculators.";

export const Route = createFileRoute("/bmi-calculator/")({
  head: () =>
    leafHead({
      path: "/bmi-calculator",
      title: TITLE,
      description: DESC,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://drhint.com/" },
              { "@type": "ListItem", position: 2, name: "Age Calculator", item: "https://drhint.com/age-calculator" },
              { "@type": "ListItem", position: 3, name: "BMI Calculator Directory", item: "https://drhint.com/bmi-calculator" },
            ],
          }),
        },
      ],
    }),
  component: BmiHubPage,
});

function BmiHubPage() {
  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / BMI Directory
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">BMI Calculator Directory</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          200 hyper-targeted BMI pages — pick your age, gender-specific, exact height, or South Asian (WHO Asian cut-off)
          variant. Every page ships a live BMI calculator, physiological context, and preventive-action framework tailored
          to that demographic.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-4">
        <BmiCalculatorIsland cutoff="standard" defaultAge={30} defaultHeightFeet={5} defaultHeightInches={7} />
      </section>

      <section className="mx-auto max-w-5xl px-4 mt-8">
        <BmiHubDirectory />
      </section>

      <section className="mx-auto max-w-3xl px-4 mt-10">
        <h2 className="text-xl md:text-2xl font-bold mb-3">How this directory is organised</h2>
        <p>
          The 200 slugs are grouped into four physiologically distinct clusters. Cluster A is the standard adult
          calculator across ages 11–60 with mixed units. Cluster B tunes copy and risk framing for female physiology
          from age 18 to 67. Cluster C is anchored on <em>exact height</em> (feet + inches) — useful when you want
          absolute weight bands rather than an age-linked calculator. Cluster D applies the WHO Asian cut-offs
          (overweight ≥23, obese ≥27.5) that better reflect visceral-adiposity risk in South Asian bodies.
        </p>
        <p className="mt-3">
          Not sure which one fits? Start with the standard calculator above — every page in the directory shares the
          same computation engine underneath, so the differences are in <em>interpretation</em> and
          <em> preventive protocol</em>, not the arithmetic.
        </p>
      </section>
    </Layout>
  );
}